// TokenRecoveryService.js
import { tokenManager } from './TokenManager';
import { secureTokenStorage } from './SecureTokenStorage';
import { Preferences } from '@capacitor/preferences';
import { isCapacitor } from '../utils/platform';
import { tokenLog, tokenWarn, tokenError } from './Debug';

class TokenRecoveryService {
  constructor() {
    this.recoveryAttempts = 0;
    this.maxRecoveryAttempts = 3;
    this.lastRecoveryTime = 0;
    this.errorLog = [];
  }

  // Log token-related errors with context
  logError(context, error, additionalInfo = {}) {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      context,
      error: error.message || error,
      stack: error.stack,
      ...additionalInfo,
      platform: isCapacitor ? 'iOS/Android' : 'Web',
      tokenPresent: !!tokenManager.getToken(),
      tokenExpiring: tokenManager.isExpiringSoon()
    };

    this.errorLog.push(errorEntry);
    
    // Keep only last 50 errors
    if (this.errorLog.length > 50) {
      this.errorLog.shift();
    }

    tokenError(`[TokenRecovery] ${context}`, errorEntry);
    
    // Send to analytics/monitoring if available
    this.sendErrorToAnalytics(errorEntry);
  }

  // Attempt to recover lost tokens
  async attemptTokenRecovery() {
    
    
    // Prevent rapid recovery attempts
    const timeSinceLastAttempt = Date.now() - this.lastRecoveryTime;
    if (timeSinceLastAttempt < 5000) {
      
      return false;
    }

    this.lastRecoveryTime = Date.now();
    this.recoveryAttempts++;

    if (this.recoveryAttempts > this.maxRecoveryAttempts) {
      this.logError('Recovery Failed', new Error('Max recovery attempts exceeded'));
      return false;
    }

    try {
      // Step 1: Check secure storage
      if (isCapacitor) {
        
        const secureTokens = await secureTokenStorage.getSecureTokens();
        
        if (secureTokens && secureTokens.accessToken) {
          
          
          // Validate tokens are not expired
          if (secureTokens.expiration > Date.now()) {
            // Restore to TokenManager
            await tokenManager.setToken(
              secureTokens.accessToken,
              Math.floor((secureTokens.expiration - Date.now()) / 1000)
            );
            
            // Restore refresh token
            if (secureTokens.refreshToken) {
              await Preferences.set({ key: 'refresh_token', value: secureTokens.refreshToken });
            }
            
            this.recoveryAttempts = 0; // Reset counter on success
            
            tokenLog('attemptTokenRecovery(): restored from secure storage');
            return true;
          }
        }
      }

      // Step 2: Check all possible storage locations
      const storageLocations = [
        { name: 'Preferences-access', key: 'access_token', type: 'preferences' },
        { name: 'Preferences-refresh', key: 'refresh_token', type: 'preferences' },
        { name: 'LocalStorage-access', key: 'accessToken', type: 'localStorage' },
        { name: 'LocalStorage-refresh', key: 'refreshToken', type: 'localStorage' },
        { name: 'Backup-tokens', key: 'backup_tokens', type: 'preferences' }
      ];

      const foundTokens = {};
      
      for (const location of storageLocations) {
        try {
          let value = null;
          
          if (location.type === 'preferences' && isCapacitor) {
            const result = await Preferences.get({ key: location.key });
            value = result.value;
          } else if (location.type === 'localStorage') {
            value = localStorage.getItem(location.key);
          }
          
          if (value) {
            foundTokens[location.name] = value;
            
          }
        } catch (error) {
          this.logError(`Check ${location.name}`, error);
        }
      }

      // Step 3: Try to reconstruct auth state from found tokens
      if (Object.keys(foundTokens).length > 0) {
        
        
        // If we have a refresh token, try to use it
        const refreshToken = foundTokens['Preferences-refresh'] || 
                           foundTokens['LocalStorage-refresh'];
        
        if (refreshToken) {
          
          
          // Import UserService dynamically to avoid circular dependency
          const { UserService } = await import('./UserService');
          const userService = new UserService();
          
          try {
            await userService.refreshToken();
            this.recoveryAttempts = 0; // Reset counter on success
            
            tokenLog('attemptTokenRecovery(): refresh succeeded using found refresh token');
            return true;
          } catch (refreshError) {
            this.logError('Refresh during recovery', refreshError);
          }
        }
      }

      // Step 4: Last resort - check for device trust
      if (isCapacitor) {
        const { value: deviceTrusted } = await Preferences.get({ key: 'device_trusted' });
        if (deviceTrusted === 'true') {
          
          // Could implement biometric re-authentication here
        }
      }

      tokenWarn('attemptTokenRecovery(): failed');
      return false;
    } catch (error) {
      this.logError('Recovery Attempt', error);
      return false;
    }
  }

  // Clear recovery state
  resetRecoveryState() {
    this.recoveryAttempts = 0;
    this.lastRecoveryTime = 0;
  }

  // Get error log for debugging
  getErrorLog() {
    return [...this.errorLog];
  }

  // Send errors to analytics (placeholder)
  sendErrorToAnalytics(errorEntry) {
    // Implementation would depend on analytics service
    // For now, just store in session storage for debugging
    try {
      const errors = JSON.parse(sessionStorage.getItem('tokenErrors') || '[]');
      errors.push(errorEntry);
      
      // Keep only last 20 errors in session
      if (errors.length > 20) {
        errors.shift();
      }
      
      sessionStorage.setItem('tokenErrors', JSON.stringify(errors));
    } catch (e) {
      // Ignore storage errors
    }
  }

  // Debug helper to dump all token-related storage
  async dumpTokenStorage() {
    const dump = {
      timestamp: new Date().toISOString(),
      tokenManager: {
        hasToken: !!tokenManager.getToken(),
        isExpiring: tokenManager.isExpiringSoon(),
        storageError: tokenManager.storageError
      },
      storage: {}
    };

    if (isCapacitor) {
      try {
        const keys = ['access_token', 'refresh_token', 'access_token_exp', 'backup_tokens'];
        for (const key of keys) {
          const { value } = await Preferences.get({ key });
          dump.storage[`Preferences:${key}`] = value ? 'present' : 'missing';
        }
      } catch (e) {
        dump.storage.preferencesError = e.message;
      }
    }

    // Check localStorage
    dump.storage['localStorage:accessToken'] = localStorage.getItem('accessToken') ? 'present' : 'missing';
    dump.storage['localStorage:refreshToken'] = localStorage.getItem('refreshToken') ? 'present' : 'missing';

    
    return dump;
  }
}

export const tokenRecoveryService = new TokenRecoveryService();